import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Plus, Trash2, Save, RotateCcw } from 'lucide-react';
import { NODE_META, COLOR_CLASSES, NODE_DEFAULTS } from './nodeTypes';
import { TIMEZONE_GROUPS } from '../shared/timezones';
import LiveTimezoneClock from '../agent/LiveTimezoneClock';

/* Right-side slide-out drawer.
   • Header is sticky at the top with the node label, an inline rename
     input AND quick Save/Delete buttons so users can act without
     scrolling to the bottom of long config forms.
   • Body scrolls independently between the two action bars.
   • Footer is `sticky bottom-0` so Save/Cancel/Reset are always visible
     even if the form runs long. */
export default function NodeConfigDrawer({ node, agents, phoneNumber, phoneNumbers, currentFlowId, onPhoneNumberChange, onClose, onSave, onDelete }) {
  const [draft, setDraft] = useState(node?.data || {});
  // Local draft of the bound inbound number — Start nodes carry no per-node
  // data, so this lives separately and is committed via onPhoneNumberChange.
  const [phoneDraft, setPhoneDraft] = useState(phoneNumber || '');
  useEffect(() => { setDraft(node?.data || {}); }, [node]);
  useEffect(() => { setPhoneDraft(phoneNumber || ''); }, [phoneNumber]);

  if (!node) return null;
  const meta = NODE_META[node.type] || { label: node.type, color: 'sky' };
  const c = COLOR_CLASSES[meta.color];

  const update = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const reset = () => setDraft({ ...(NODE_DEFAULTS[node.type] || {}) });
  const save = () => {
    if (node.type === 'start' && phoneDraft && phoneDraft !== phoneNumber) {
      onPhoneNumberChange?.(phoneDraft);
    }
    onSave({ ...node, data: draft });
  };

  return (
    <motion.aside
      initial={{ x: 480, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 480, opacity: 0 }}
      transition={{ type: 'spring', damping: 30, stiffness: 280 }}
      className="absolute top-0 right-0 bottom-0 w-[460px] bg-[#0c0d12] border-l border-white/10 flex flex-col z-50"
      data-testid={`node-config-drawer-${node.type}`}
    >
      {/* Header — sticky top with quick actions */}
      <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2 bg-[#0c0d12]">
        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider border ${c.bg} ${c.text} ${c.border}`}>
          {meta.label.toUpperCase()}
        </span>
        <input
          value={draft.label || ''}
          onChange={(e) => update('label', e.target.value)}
          placeholder="Node label (optional)"
          className="flex-1 bg-transparent text-white text-sm focus:outline-none placeholder:text-gray-500"
        />
        <button
          onClick={save}
          className="p-1.5 rounded-lg bg-teal-400 text-[#08090d] hover:bg-teal-500"
          title="Save"
          data-testid="drawer-save-top"
        >
          <Save size={13} />
        </button>
        {node.type !== 'start' && (
          <button
            onClick={onDelete}
            className="p-1.5 rounded-lg text-rose-300 border border-rose-400/30 hover:bg-rose-500/10"
            title="Delete node"
            data-testid="drawer-delete-top"
          >
            <Trash2 size={13} />
          </button>
        )}
        <button onClick={onClose} className="text-gray-400 hover:text-white p-1" data-testid="drawer-close"><X size={16} /></button>
      </div>

      {/* Body — scrolls between the two sticky action bars */}
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {renderForm(node.type, draft, update, agents, phoneDraft, setPhoneDraft, phoneNumbers, phoneNumber, currentFlowId)}
      </div>

      {/* Footer — pinned to bottom of drawer */}
      <div className="px-5 py-3 border-t border-white/10 flex items-center gap-2 bg-[#0c0d12] sticky bottom-0">
        {node.type !== 'start' && (
          <button onClick={onDelete} className="px-3 py-2 rounded-lg text-rose-300 border border-rose-400/30 text-xs hover:bg-rose-500/10" data-testid="drawer-delete">
            <Trash2 size={12} className="inline mr-1.5" /> Delete
          </button>
        )}
        <button onClick={reset} className="px-3 py-2 rounded-lg text-gray-300 border border-white/10 text-xs hover:bg-white/5" data-testid="drawer-reset">
          <RotateCcw size={12} className="inline mr-1.5" /> Reset
        </button>
        <div className="flex-1" />
        <button onClick={onClose} className="px-3 py-2 rounded-lg text-gray-300 text-xs hover:text-white">Cancel</button>
        <button onClick={save} className="px-4 py-2 rounded-lg bg-teal-400 text-[#08090d] text-xs font-bold hover:bg-teal-500" data-testid="drawer-save">Save</button>
      </div>
    </motion.aside>
  );
}

function renderForm(type, d, set, agents, phoneDraft, setPhoneDraft, phoneNumbers, phoneNumber, currentFlowId) {
  switch (type) {
    case 'start': {
      // Start carries no per-node data — it just surfaces (and lets the
      // user change) the inbound number this flow is bound to. Number
      // changes commit on Save via onPhoneNumberChange.
      const list = Array.isArray(phoneNumbers) ? phoneNumbers : [];
      const isInList = list.some(p => (p.phone_number || p.number) === phoneDraft);
      const valid = !phoneDraft || /^\+\d{8,15}$/.test(phoneDraft);
      // Show what the picked number is currently routed to so the user
      // sees the conflict BEFORE saving.
      const pickedRow = list.find(p => (p.phone_number || p.number) === phoneDraft);
      const conflictAgent = pickedRow?.agent_name || pickedRow?.agent_id;
      const conflictFlow = pickedRow?.routing_flow_id;
      const fmtRouting = (p) => {
        if (p.routing_flow_id) return `→ Flow: ${p.routing_flow_name || 'unnamed'}`;
        if (p.agent_id) return `→ Agent: ${p.agent_name || p.agent_id.slice(0, 12)}`;
        return '→ Unassigned';
      };
      return (
        <>
          <Field label="Inbound number">
            {list.length > 0 && (
              <select
                value={isInList ? phoneDraft : '__custom__'}
                onChange={e => { if (e.target.value !== '__custom__') setPhoneDraft(e.target.value); }}
                className={inputCls}
                data-testid="field-inbound-number-select"
              >
                {list.map(p => {
                  const num = p.phone_number || p.number;
                  const prov = (p.provider || '').toLowerCase();
                  return <option key={num} value={num}>{num}{prov ? ` · ${prov}` : ''} {fmtRouting(p)}</option>;
                })}
                <option value="__custom__">— Enter a different number —</option>
              </select>
            )}
            <input
              value={phoneDraft || ''}
              onChange={e => setPhoneDraft(e.target.value.trim())}
              placeholder="+13035551234"
              className={`${inputCls} ${list.length > 0 ? 'mt-2' : ''}`}
              data-testid="field-inbound-number"
            />
            {!valid && (
              <p className="text-[10px] text-amber-300 mt-1">Use E.164 format (e.g. +13035551234)</p>
            )}
          </Field>

          {/* Conflict warning — shows BEFORE save so the user can back out */}
          {pickedRow && phoneDraft !== phoneNumber && (conflictAgent || (conflictFlow && conflictFlow !== currentFlowId)) && (
            <div className="rounded-lg border border-amber-400/40 bg-amber-400/10 p-3 text-[11px] text-amber-200" data-testid="phone-conflict-warning">
              <div className="font-bold mb-1">⚠ This number is currently {conflictAgent ? `routed to AI Agent "${conflictAgent}"` : `bound to another routing flow`}.</div>
              <div className="opacity-90">Saving will detach it from there and re-point its provider webhook at this canvas. Make sure that's what you want.</div>
            </div>
          )}

          <p className="text-[11px] text-gray-400 leading-snug">
            Calls to this number flow into the canvas. Configure the greeting on the AI Agent or Menu node downstream — Start has no greeting of its own. Click <span className="text-teal-300 font-semibold">Save</span> after changing the number to bind the flow.
          </p>
        </>
      );
    }
    case 'menu': {
      const opts = d.options || [];
      const setOpt = (i, k, v) => {
        const next = [...opts]; next[i] = { ...next[i], [k]: v }; set('options', next);
      };
      const addOpt = () => set('options', [...opts, { key: String(opts.length + 1), label: '', handle_id: `key_${opts.length + 1}` }]);
      const rmOpt = (i) => set('options', opts.filter((_, x) => x !== i));
      return (
        <>
          <Field label="Spoken prompt"><textarea value={d.prompt || ''} onChange={e => set('prompt', e.target.value)} rows={2} className={inputCls} placeholder="Press 1 for sales, 2 for support…" /></Field>
          <Field label={`Timeout (${d.timeout || 10}s)`}>
            <input type="number" min={3} max={30} value={d.timeout || 10} onChange={e => set('timeout', parseInt(e.target.value) || 10)} className={inputCls} />
          </Field>

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">Branch options</label>
            <div className="space-y-2">
              {opts.map((o, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input value={o.key || ''} onChange={e => setOpt(i, 'key', e.target.value)} maxLength={1} placeholder="1" className={`${inputCls} w-12 text-center`} />
                  <input value={o.label || ''} onChange={e => setOpt(i, 'label', e.target.value)} placeholder="Label (e.g. Sales)" className={`${inputCls} flex-1`} />
                  <button onClick={() => rmOpt(i)} className="p-1.5 text-rose-300 hover:bg-rose-500/10 rounded"><Trash2 size={12} /></button>
                </div>
              ))}
              <button onClick={addOpt} className="w-full px-3 py-2 rounded-lg border border-dashed border-white/15 text-[11px] text-gray-300 hover:bg-white/5"><Plus size={11} className="inline mr-1" />Add option</button>
            </div>
          </div>

          {/* Reserved navigation keys — handled inline, no output edges */}
          <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-wider text-gray-400">Navigation shortcuts</div>
            <Toggle label={`Press ${d.repeat_key || '9'} to repeat this menu`}
              checked={!!d.repeat_enabled} onChange={v => set('repeat_enabled', v)} testid="menu-repeat-toggle" />
            {d.repeat_enabled && (
              <input value={d.repeat_key || '9'} onChange={e => set('repeat_key', (e.target.value || '').slice(0, 1))}
                maxLength={1} placeholder="9" className={`${inputCls} w-16 text-center`} data-testid="menu-repeat-key" />
            )}
            <Toggle label={`Press ${d.main_menu_key || '0'} to return to main menu`}
              checked={!!d.main_menu_enabled} onChange={v => set('main_menu_enabled', v)} testid="menu-mainmenu-toggle" />
            {d.main_menu_enabled && (
              <input value={d.main_menu_key || '0'} onChange={e => set('main_menu_key', (e.target.value || '').slice(0, 1))}
                maxLength={1} placeholder="0" className={`${inputCls} w-16 text-center`} data-testid="menu-mainmenu-key" />
            )}
            <p className="text-[10px] text-gray-500 leading-snug pt-1">
              These keys are routed inline — no extra edges to wire. "Main menu" jumps to the first Menu node in the flow.
            </p>
          </div>
        </>
      );
    }
    case 'time_router': {
      const wins = d.windows || [];
      const setWin = (i, k, v) => { const next = [...wins]; next[i] = { ...next[i], [k]: v }; set('windows', next); };
      const toggleDay = (i, day) => {
        const cur = wins[i].days || [];
        setWin(i, 'days', cur.includes(day) ? cur.filter(x => x !== day) : [...cur, day]);
      };
      const addWin = () => set('windows', [...wins, { days: ['mon','tue','wed','thu','fri'], start: '09:00', end: '17:00' }]);
      const days = ['mon','tue','wed','thu','fri','sat','sun'];
      return (
        <>
          <Field
            label="Timezone"
            hint="Used to evaluate the current day & time on inbound calls. The clock below shows what 'now' looks like in this zone — confirm it before saving."
          >
            <select
              value={d.timezone || 'America/New_York'}
              onChange={e => set('timezone', e.target.value)}
              className={inputCls}
              data-testid="time-router-timezone"
            >
              {TIMEZONE_GROUPS.map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </Field>
          <LiveTimezoneClock
            timezone={d.timezone || 'America/New_York'}
            textPrimary="text-white"
            textSecondary="text-white/60"
            cardBorder="border-white/10"
          />
          <div className="space-y-3">
            {wins.map((w, i) => (
              <div key={i} className="rounded-lg border border-white/10 p-3 space-y-2">
                <div className="flex flex-wrap gap-1">
                  {days.map(day => (
                    <button key={day} onClick={() => toggleDay(i, day)} className={`px-2 py-1 rounded text-[10px] font-semibold uppercase ${(w.days || []).includes(day) ? 'bg-teal-400 text-[#08090d]' : 'bg-white/5 text-gray-300 hover:bg-white/10'}`}>{day}</button>
                  ))}
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <input type="time" value={w.start || '09:00'} onChange={e => setWin(i, 'start', e.target.value)} className={inputCls} />
                  <span className="text-gray-400">→</span>
                  <input type="time" value={w.end || '17:00'} onChange={e => setWin(i, 'end', e.target.value)} className={inputCls} />
                  {wins.length > 1 && <button onClick={() => set('windows', wins.filter((_, x) => x !== i))} className="p-1 text-rose-300 hover:bg-rose-500/10 rounded"><Trash2 size={11} /></button>}
                </div>
              </div>
            ))}
            <button onClick={addWin} className="w-full px-3 py-2 rounded-lg border border-dashed border-white/15 text-[11px] text-gray-300 hover:bg-white/5"><Plus size={11} className="inline mr-1" />Add window</button>
          </div>
          <div
            data-testid="time-router-boundary-note"
            className="mt-3 rounded-lg border border-sky-400/20 bg-sky-400/5 p-3 text-[11px] leading-relaxed text-sky-100/80"
          >
            <div className="font-semibold text-sky-200 mb-1">How time windows are matched</div>
            Both endpoints are <span className="font-semibold text-white">inclusive</span>.
            For a window of <code className="px-1 rounded bg-white/10">09:00 → 17:00</code>:
            <ul className="mt-1 ml-3 list-disc space-y-0.5">
              <li><span className="text-emerald-300 font-medium">Open</span> at 09:00:00 and still <span className="text-emerald-300 font-medium">open</span> at exactly 17:00:00.</li>
              <li><span className="text-rose-300 font-medium">Closed</span> from 17:01 onward.</li>
            </ul>
            Tip: to close exactly at 5pm sharp, set the end time to <code className="px-1 rounded bg-white/10">16:59</code>.
          </div>
        </>
      );
    }
    case 'tag_filter':
      return <Field label="LeadConnector contact tag"><input value={d.tag || ''} onChange={e => set('tag', e.target.value)} placeholder="e.g. lead-magnet" className={inputCls} /></Field>;
    case 'contact_filter':
      return <p className="text-xs text-gray-400">Branches based on whether the caller's phone matches an existing LeadConnector contact in this sub-account. No config needed.</p>;
    case 'ai_agent':
      return (
        <>
          <Field label="Agent">
            <select value={d.agent_id || ''} onChange={e => {
              const a = (agents || []).find(a => a.agent_id === e.target.value);
              set('agent_id', e.target.value);
              if (a) set('agent_name', a.name);
            }} className={inputCls} data-testid="field-agent-select">
              <option value="">— Pick an agent —</option>
              {(agents || []).map(a => <option key={a.agent_id} value={a.agent_id}>{a.name}</option>)}
            </select>
          </Field>
          <Field label="Max duration (seconds)"><input type="number" min={30} max={3600} value={d.max_duration_seconds || 600} onChange={e => set('max_duration_seconds', parseInt(e.target.value) || 600)} className={inputCls} /></Field>
        </>
      );
    case 'transfer':
      return (
        <>
          <Field label="Forward to (E.164)"><input value={d.number || ''} onChange={e => set('number', e.target.value)} placeholder="+13035551234" className={inputCls} data-testid="field-transfer-number" /></Field>
          <Field label={`Ring timeout (${d.ring_timeout || 20}s before falling through "No answer")`}>
            <input
              type="number" min={5} max={120}
              value={d.ring_timeout || 20}
              onChange={e => set('ring_timeout', parseInt(e.target.value) || 20)}
              className={inputCls}
              data-testid="field-transfer-ring-timeout"
            />
          </Field>
          <label className="flex items-start gap-2.5 px-2.5 py-2.5 rounded-lg border border-white/10 bg-white/[0.03] cursor-pointer hover:bg-white/5 transition-colors">
            <input
              type="checkbox"
              checked={!!d.record_call}
              onChange={e => set('record_call', e.target.checked)}
              className="mt-0.5 accent-teal-400"
              data-testid="field-transfer-record-call"
            />
            <div className="flex-1">
              <div className="text-xs font-semibold text-white">Record this transfer</div>
              <div className="text-[10.5px] text-gray-400 leading-snug mt-0.5">
                Saves a dual-channel recording, then runs Whisper transcription + AI summary. The transferred call will show up in the Contact Timeline with playback, transcript &amp; summary — just like direct-AI calls.
              </div>
              <div className="text-[10px] text-amber-300/90 mt-1.5">
                ⚠ Check your local laws — some US states require all parties to be notified of recording.
              </div>
            </div>
          </label>
          <p className="text-[11px] text-gray-400 leading-snug">
            Connect the <span className="text-rose-300 font-semibold">No answer / busy</span> handle to an AI Agent so the caller is never hung up on if the team isn't available.
          </p>
          <p className="text-[10.5px] text-gray-500 leading-snug">
            Caller ID: the office sees the <span className="text-teal-300 font-semibold">original caller's number</span>, not your Twilio line — so they know who's actually calling.
          </p>
        </>
      );
    case 'hangup':
      return <Field label="Optional farewell"><input value={d.farewell || ''} onChange={e => set('farewell', e.target.value)} placeholder="Goodbye!" className={inputCls} /></Field>;
    default:
      return null;
  }
}

const inputCls = "w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-teal-400/50";

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">{label}</label>
      {children}
      {hint && <p className="mt-1.5 text-[10px] text-gray-500 leading-relaxed">{hint}</p>}
    </div>
  );
}
function Toggle({ label, checked, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} className="flex items-center gap-2 group" data-testid={testid}>
      <span className={`w-9 h-5 rounded-full transition relative ${checked ? 'bg-teal-400' : 'bg-white/10'}`}>
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition ${checked ? 'translate-x-4' : ''}`} />
      </span>
      <span className="text-xs text-gray-300 group-hover:text-white">{label}</span>
    </button>
  );
}
